/**
 * MultiAddAssetResultStep — terminal step of the asset wizard.
 *
 * Mirrors `MultiAddResultStep` but speaks in assets, not activities.
 * The wizard's submit creates one or more `Asset` records (each with
 * its own activities) and hands us the asset ids + import ids. We
 * aggregate CO₂e by querying activities that share those import ids
 * (same pattern as the activity flow) and surface asset-centric CTAs:
 * "View Asset" → asset dashboard (single), "View Assets" → asset list
 * (many).
 */

import { useMemo } from 'react';
import { CheckCircle, AlertTriangle, ExternalLink, List } from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { cn } from '@g17eco/ui-modern/lib/utils';
import {
  CalculationStatus,
  type EmissionEvidence,
} from '../../../types/emissions';
import { EVIDENCE_TYPE_LABEL, getMimeIcon } from '../../../utils/evidenceDisplay';
import { useGetActivitiesQuery } from '../../../api/emissions';
import type { FormattedEmission } from '../../../utils/emissionsFormatter';

const PAGE_SIZE = 100;

interface MultiAddAssetResultStepProps {
  initiativeId: string;
  /** BulkImport ids returned by the asset submit. */
  importIds: string[];
  /** Asset ids created across all of the imports. */
  assetIds: string[];
  /** Evidences linked to the batch in the preceding Attach step. */
  attachedEvidences: EmissionEvidence[];
  formatCompact: (valueInKg: number | null | undefined) => FormattedEmission;
  onViewAssets: () => void;
  onAddAnother: () => void;
  /**
   * Jump straight to a single asset's dashboard. The wizard promotes
   * this when only one asset was created — same UX shortcut as the
   * activity flow.
   */
  onViewAsset?: (assetId: string) => void;
}

export const MultiAddAssetResultStep = ({
  initiativeId,
  importIds,
  assetIds,
  attachedEvidences,
  formatCompact,
  onViewAssets,
  onAddAnother,
  onViewAsset,
}: MultiAddAssetResultStepProps) => {
  // Same pattern as MultiAddResultStep: pull the activities created
  // by this batch so we can sum CO₂e and surface calculation health.
  const { data, isFetching } = useGetActivitiesQuery(
    {
      initiativeId,
      bulkImportIds: importIds.join(','),
      limit: PAGE_SIZE,
      offset: 0,
    },
    { skip: importIds.length === 0 },
  );

  const activities = data?.data ?? [];
  const assetCount = assetIds.length;

  const { totalCo2e, calculatedCount, failedCount, pendingCount } = useMemo(() => {
    let total = 0;
    let calculated = 0;
    let failed = 0;
    let pending = 0;
    for (const a of activities) {
      if (a.calculationStatus === CalculationStatus.Calculated && typeof a.calculatedCO2e === 'number') {
        total += a.calculatedCO2e;
        calculated += 1;
      } else if (a.calculationStatus === CalculationStatus.Failed) {
        failed += 1;
      } else {
        pending += 1;
      }
    }
    return { totalCo2e: total, calculatedCount: calculated, failedCount: failed, pendingCount: pending };
  }, [activities]);

  const hasIssues = failedCount > 0 || pendingCount > 0;
  const totalFormatted = formatCompact(totalCo2e);

  const uniqueEvidences = useMemo(
    () => Array.from(new Map(attachedEvidences.map((ev) => [ev._id, ev])).values()),
    [attachedEvidences],
  );

  const singleAssetId = assetCount === 1 ? assetIds[0] : null;

  return (
    <Card>
      <CardContent className='tw:p-8 tw:text-center'>
        <div
          className={cn(
            'tw:rounded-full tw:inline-flex tw:items-center tw:justify-center tw:mb-4',
            hasIssues ? 'tw:bg-amber-100' : 'tw:bg-emerald-100',
          )}
          style={{ width: 64, height: 64 }}
        >
          {hasIssues ? (
            <AlertTriangle className='tw:size-8 tw:text-amber-600' />
          ) : (
            <CheckCircle className='tw:size-8 tw:text-emerald-600' />
          )}
        </div>

        <h5 className='tw:font-semibold tw:mb-2'>
          {assetCount} {assetCount === 1 ? 'Asset' : 'Assets'} Created
        </h5>

        {/* Activity sub-count — gives the user a sense of how much
            activity data was attached to the assets. */}
        {activities.length > 0 && (
          <p className='tw:text-sm tw:text-muted-foreground tw:mb-3'>
            with {activities.length} {activities.length === 1 ? 'activity' : 'activities'}
          </p>
        )}

        {/* Summed CO2e — only meaningful when at least one calculated */}
        {calculatedCount > 0 && (
          <div className='tw:mb-4'>
            <span className='tw:text-3xl tw:font-bold'>{totalFormatted.formatted}</span>
            <span className='tw:text-muted-foreground tw:ml-2'>{totalFormatted.unitCompact}</span>
            {calculatedCount < activities.length && (
              <p className='tw:text-xs tw:text-muted-foreground tw:mt-1 tw:mb-0'>
                Sum across {calculatedCount} of {activities.length} activities
              </p>
            )}
          </div>
        )}

        {isFetching && activities.length === 0 && (
          <p className='tw:text-sm tw:text-muted-foreground tw:mb-4'>Loading results…</p>
        )}

        {/* Status breakdown — only shown when something needs attention */}
        {hasIssues && (
          <div className='tw:rounded-lg tw:border tw:border-amber-200 tw:bg-amber-50 tw:p-4 tw:mb-4 tw:text-left'>
            <h6 className='tw:font-semibold tw:text-amber-800 tw:text-sm tw:mb-2 tw:flex tw:items-center tw:gap-2'>
              <AlertTriangle className='tw:size-4' />
              Some activities need attention
            </h6>
            <ul className='tw:text-sm tw:text-amber-700 tw:space-y-1 tw:list-disc tw:list-inside tw:mb-0'>
              {failedCount > 0 && (
                <li>{failedCount} {failedCount === 1 ? 'activity has' : 'activities have'} a failed calculation — open the asset to review.</li>
              )}
              {pendingCount > 0 && (
                <li>{pendingCount} {pendingCount === 1 ? 'activity is' : 'activities are'} still calculating.</li>
              )}
            </ul>
          </div>
        )}

        {/* Attached evidence — confirms the Attach step landed. */}
        {uniqueEvidences.length > 0 && (
          <div className='tw:rounded-lg tw:border tw:border-border tw:p-4 tw:mb-4 tw:text-left'>
            <h6 className='tw:font-semibold tw:text-sm tw:mb-2'>
              Evidence attached ({uniqueEvidences.length})
            </h6>
            <ul className='tw:divide-y tw:divide-border tw:text-sm'>
              {uniqueEvidences.map((ev) => (
                <li key={ev._id} className='tw:flex tw:items-center tw:gap-2 tw:py-2'>
                  <i className={`fa-regular ${getMimeIcon(ev.mimeType)} tw:text-ThemeTextMedium`} aria-hidden />
                  <span className='tw:truncate tw:flex-1 tw:min-w-0'>{ev.originalName}</span>
                  <Badge variant='ghost' className='tw:text-[10px]'>
                    {EVIDENCE_TYPE_LABEL[ev.evidenceType]}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/*
         * Actions. Single-asset case promotes "View Asset" to primary
         * (jumps to its dashboard). Many-asset case promotes "View
         * Assets" instead — landing on the list page is the right
         * destination when the user just created several.
         */}
        <div className='tw:flex tw:justify-center tw:gap-3 tw:flex-wrap'>
          <Button variant='outline' onClick={onAddAnother}>
            Add Another
          </Button>
          {singleAssetId && onViewAsset ? (
            <>
              <Button variant='outline' onClick={onViewAssets}>
                <List className='tw:size-4' />
                View Assets
              </Button>
              <Button onClick={() => onViewAsset(singleAssetId)}>
                <ExternalLink className='tw:size-4' />
                View Asset
              </Button>
            </>
          ) : (
            <Button onClick={onViewAssets}>
              <List className='tw:size-4' />
              View Assets
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
